import React, { useEffect, useRef, useState } from "react";
import logoImg from "../assets/logoo.png";

export default function Splash({
  showOnce = false,
  minVisible = 3500,
  maxTimeout = 4000,
  onDone,           // ✅ NEW: called when splash fully finishes
}) {
  const [visible, setVisible] = useState(false);
  const [stage, setStage] = useState("enter");
  const [loadingDone, setLoadingDone] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const overlayRef = useRef(null);
  const parallaxRef = useRef(null);
  const boxRef = useRef(null);
  const canvasRef = useRef(null);

  const rafRef = useRef(null);
  const timersRef = useRef([]);
  const particlesRef = useRef([]);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const schedule = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  };
  const clearAllTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  useEffect(() => {
    const siteRoot = document.getElementById("site-root");
    const navbar = document.querySelector("nav");
    if (siteRoot) {
      siteRoot.__prevVisibility = siteRoot.style.visibility;
      siteRoot.__prevTransition = siteRoot.style.transition;
      siteRoot.__prevTransform = siteRoot.style.transform;
      siteRoot.__prevOpacity = siteRoot.style.opacity;
      siteRoot.style.visibility = "hidden";
      siteRoot.style.opacity = "0";
      siteRoot.style.transform = "translateY(28px)";
    }
    if (navbar) {
      navbar.__prevVisibility = navbar.style.visibility;
      navbar.__prevOpacity = navbar.style.opacity;
      navbar.style.visibility = "hidden";
      navbar.style.opacity = "0";
    }
    return () => {};
  }, []);

  useEffect(() => {
    try {
      if (showOnce && localStorage.getItem("splash_v1_shown") === "1") {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        document.body.style.height = "";
        document.documentElement.style.overflow = "";
        return;
      }
    } catch (e) {}

    let cancelled = false;

    const lockScroll = () => {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.documentElement.style.overflow = "hidden";
    };
    const unlockScroll = () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('width');
      document.body.style.removeProperty('height');
      document.documentElement.style.overflow = "";
      document.documentElement.style.removeProperty('overflow');
    };

    const start = () => {
      if (cancelled) return;
      setVisible(true);
      setStage("enter");
      setLoadingDone(false);
      setShowWelcome(false);
      lockScroll();

      schedule(() => setStage("hold"), 820);

      schedule(() => {
        const finishSequence = () => {
          setLoadingDone(true);
          schedule(() => setShowWelcome(true), 320);
          schedule(() => {
            setStage("exit");
            setTimeout(() => unlockScroll(), 0);
          }, 720);
        };

        if (document.readyState === "complete") {
          finishSequence();
        } else {
          const onLoad = () => {
            finishSequence();
            window.removeEventListener("load", onLoad);
          };
          window.addEventListener("load", onLoad, { once: true });
          schedule(() => {
            try { window.removeEventListener("load", onLoad); } catch (e) {}
            finishSequence();
          }, Math.max(0, maxTimeout - minVisible));
        }
      }, minVisible);
    };

    start();

    return () => {
      cancelled = true;
      clearAllTimers();
      unlockScroll();
    };
  }, [showOnce, minVisible, maxTimeout]);

  useEffect(() => {
    if (reducedMotion) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = canvas.offsetWidth);
    let h = (canvas.height = canvas.offsetHeight);

    const count = Math.min(48, Math.max(18, Math.floor((w * h) / 120000)));
    particlesRef.current = Array.from({ length: count }).map(() => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: 8 + Math.random() * 28,
      alpha: 0.035 + Math.random() * 0.18,
      vx: (Math.random() - 0.5) * 0.12,
      vy: -0.02 - Math.random() * 0.07,
    }));

    function resize() {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    }
    window.addEventListener("resize", resize);

    let rafId;
    function draw() {
      ctx.clearRect(0, 0, w, h);
      for (let p of particlesRef.current) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y + p.r < 0) p.y = h + p.r;
        if (p.x - p.r > w) p.x = -p.r;
        if (p.x + p.r < 0) p.x = w + p.r;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
        g.addColorStop(0, `rgba(255,245,230,${p.alpha})`);
        g.addColorStop(1, `rgba(255,245,230,0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      rafId = requestAnimationFrame(draw);
      rafRef.current = rafId;
    }

    draw();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      particlesRef.current = [];
    };
  }, [reducedMotion, visible]);

  useEffect(() => {
    if (reducedMotion) return;
    const overlay = overlayRef.current;
    const parallaxEl = parallaxRef.current;
    if (!overlay || !parallaxEl) return;
    const mul = 8;
    let raf = null;

    function onMove(e) {
      const rect = overlay.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      const nx = Math.max(-1, Math.min(1, dx));
      const ny = Math.max(-1, Math.min(1, dy));
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        parallaxEl.style.transform = `translate(${nx * mul}px, ${ny * mul}px)`;
      });
    }
    function onLeave() {
      parallaxEl.style.transition = "transform 600ms cubic-bezier(.2,.9,.2,1)";
      parallaxEl.style.transform = "translate(0px, 0px)";
      setTimeout(() => (parallaxEl.style.transition = ""), 620);
    }

    overlay.addEventListener("mousemove", onMove);
    overlay.addEventListener("mouseleave", onLeave);
    overlay.addEventListener("touchmove", (ev) => {
      if (ev.touches && ev.touches[0]) onMove(ev.touches[0]);
    });

    return () => {
      overlay.removeEventListener("mousemove", onMove);
      overlay.removeEventListener("mouseleave", onLeave);
    };
  }, [reducedMotion, visible]);

  useEffect(() => {
    if (stage !== "exit") return;

    const overlayEl = overlayRef.current;
    const box = boxRef.current;
    const navLogo = document.getElementById("nav-logo");
    const navbar = document.querySelector("nav");
    const siteRoot = document.getElementById("site-root");

    const exitDuration = 700;

    if (box) {
      box.style.transition = "none";
      box.style.transform = "translateY(0) scale(1)";
      box.style.opacity = "1";
      const _ = box.offsetHeight;
      box.style.transition = "transform 700ms cubic-bezier(.25,.46,.45,.94), opacity 650ms ease";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          box.style.transform = "translateY(-220px) scale(.86)";
          box.style.opacity = "0";
        });
      });
    }

    const captionEl = overlayEl?.querySelector(".splash-caption");
    if (captionEl) {
      captionEl.style.transition = "none";
      captionEl.style.transform = "translateY(0)";
      captionEl.style.opacity = "1";
      const _ = captionEl.offsetHeight;
      captionEl.style.transition = "transform 700ms cubic-bezier(.25,.46,.45,.94), opacity 650ms ease";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          captionEl.style.transform = "translateY(-220px)";
          captionEl.style.opacity = "0";
        });
      });
    }

    if (navLogo) {
      navLogo.style.transition = "opacity 260ms ease";
      navLogo.style.opacity = "0";
    }
    if (navbar) {
      navbar.style.visibility = "hidden";
      navbar.style.opacity = "0";
    }
    if (overlayEl) {
      overlayEl.style.transition = `opacity ${exitDuration}ms ease, background ${exitDuration}ms ease`;
      requestAnimationFrame(() => {
        overlayEl.style.opacity = "0";
        overlayEl.style.background = "transparent";
        overlayEl.style.pointerEvents = "none";
      });
    }

    try {
      const halo = overlayEl?.querySelector(".splash-halo");
      if (halo) {
        halo.style.transition = `opacity ${Math.min(exitDuration, 420)}ms ease`;
        halo.style.opacity = "0";
      }
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.style.transition = `opacity ${Math.min(exitDuration, 420)}ms ease`;
        canvas.style.opacity = "0";
        schedule(() => {
          try { canvas.style.display = "none"; } catch(e) {}
        }, Math.min(exitDuration + 40, 480));
      }
    } catch (e) {}

    const revealDelay = 650;
    const siteRevealDuration = 550;

    if (siteRoot) {
      siteRoot.style.transition = "";
      siteRoot.style.transform = "translateY(46px)";
      siteRoot.style.opacity = "0";
      siteRoot.style.visibility = "hidden";
    }

    const loaderWrap = overlayEl?.querySelector('[role="status"]');
    if (loaderWrap) {
      loaderWrap.style.transition = "none";
      loaderWrap.style.transform = "translateY(0)";
      loaderWrap.style.opacity = "1";
      const _ = loaderWrap.offsetHeight;
      loaderWrap.style.transition = "transform 700ms cubic-bezier(.25,.46,.45,.94), opacity 650ms ease";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          loaderWrap.style.transform = "translateY(-180px)";
          loaderWrap.style.opacity = "0";
        });
      });
    }

    schedule(() => {
      if (siteRoot) {
        siteRoot.style.visibility = "visible";
        siteRoot.style.transition = `transform ${siteRevealDuration}ms cubic-bezier(.2,.9,.2,1), opacity ${siteRevealDuration}ms ease`;
        requestAnimationFrame(() => {
          siteRoot.style.transform = "translateY(0)";
          siteRoot.style.opacity = "1";
        });
      }
      if (navbar) {
        navbar.style.visibility = "visible";
        navbar.style.transition = `opacity ${siteRevealDuration}ms ease`;
        navbar.style.opacity = "1";
      }
      if (navLogo) {
        navLogo.style.transition = "opacity 360ms ease";
        navLogo.style.opacity = "1";
      }
    }, revealDelay);

    schedule(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearAllTimers();
      particlesRef.current = [];

      if (siteRoot) {
        siteRoot.style.transition = "";
        siteRoot.style.transform = "";
        siteRoot.style.opacity = "";
        siteRoot.style.visibility = "";
      }
      if (navbar) {
        navbar.style.transition = "";
        navbar.style.opacity = "";
        navbar.style.visibility = "";
      }
      if (navLogo) {
        navLogo.style.transition = "";
        navLogo.style.opacity = "";
      }
      try {
        if (overlayEl && overlayEl.parentNode) {
          overlayEl.style.display = "none";
        }
      } catch (e) {}

      try {
        if (showOnce) localStorage.setItem("splash_v1_shown", "1");
      } catch (e) {}

      setVisible(false);

      // ✅ Notify App.js that the splash is fully done
      if (typeof onDone === "function") onDone();

    }, revealDelay + Math.max(exitDuration, siteRevealDuration) + 120);

    return () => {
      clearAllTimers();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stage, showOnce, onDone]);

  if (!visible) return null;

  const CIRCUM = 2 * Math.PI * 22;

  return (
    <>
      <style>{`
        @keyframes logo-zoom { 0% { transform: scale(.72); opacity: 0; filter: blur(4px); } 60% { transform: scale(1.06); opacity: 1; filter: blur(0); } 100% { transform: scale(1.0); opacity: 1; filter: blur(0); } }
        @keyframes logo-pulse { 0% { transform: scale(1); } 50% { transform: scale(1.02); } 100% { transform: scale(1); } }
        @keyframes fadeInCaption { 0% { opacity: 0; transform: translateY(15px) scale(0.96); filter: blur(4px); } 60% { opacity: 1; transform: translateY(0) scale(1.02); filter: blur(0); } 100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); } }
        @keyframes captionPulse { 0% { transform: scale(1); opacity: 1; filter: brightness(1); } 50% { transform: scale(1.03); opacity: 0.92; filter: brightness(1.08); } 100% { transform: scale(1); opacity: 1; filter: brightness(1); } }
        @keyframes spin-rot { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
        @keyframes glow-pulse { 0%, 100% { box-shadow: 0 30px 60px rgba(16,24,40,0.12), 0 10px 24px rgba(16,24,40,0.06), 0 0 30px rgba(255,215,0,0.15), 0 0 60px rgba(255,215,0,0.08); } 50% { box-shadow: 0 30px 60px rgba(16,24,40,0.12), 0 10px 24px rgba(16,24,40,0.06), 0 0 40px rgba(255,215,0,0.2), 0 0 75px rgba(255,215,0,0.12); } }
        @keyframes loader-glow { 0%, 100% { filter: drop-shadow(0 0 6px rgba(138,138,138,0.3)) drop-shadow(0 0 12px rgba(138,138,138,0.15)); } 50% { filter: drop-shadow(0 0 8px rgba(138,138,138,0.4)) drop-shadow(0 0 16px rgba(138,138,138,0.2)); } }
        @keyframes loader-glow-done { 0%, 100% { filter: drop-shadow(0 0 8px rgba(22,163,74,0.4)) drop-shadow(0 0 16px rgba(22,163,74,0.2)); } 50% { filter: drop-shadow(0 0 12px rgba(22,163,74,0.5)) drop-shadow(0 0 20px rgba(22,163,74,0.25)); } }
        @keyframes halo-radiate { 0% { transform: scale(1); opacity: 1; filter: blur(28px) saturate(110%); } 50% { transform: scale(1.15); opacity: 0.85; filter: blur(35px) saturate(120%); } 100% { transform: scale(1); opacity: 1; filter: blur(28px) saturate(110%); } }
        .splash-halo { position: absolute; width: 360px; height: 360px; border-radius: 50%; background: radial-gradient(circle, rgba(255,245,230,0.95) 0%, rgba(255,245,230,0.45) 28%, rgba(255,245,230,0.08) 58%, transparent 75%); filter: blur(28px) saturate(110%); pointer-events: none; z-index: 2; animation: halo-radiate 3500ms ease-in-out infinite; }
        .spinner-rotate { animation: spin-rot 1100ms linear infinite; transform-origin:center; }
        .circle-done { stroke: #16a34a !important; transition: stroke 240ms ease, stroke-dashoffset 520ms cubic-bezier(.2,.9,.2,1); }
        .check-text { color:#166534; font-weight:600; font-size:16px; opacity:0; transform: translateY(6px); transition: opacity 320ms ease, transform 320ms ease; }
        .check-text.show { opacity:1; transform: translateY(0); }
        #site-root { transform: translateY(28px); opacity:0; transition: transform 760ms cubic-bezier(.2,.9,.2,1), opacity 760ms ease; }
        #site-root.site-visible { transform: translateY(0); opacity: 1; }
      `}</style>

      <div
        ref={overlayRef}
        style={{
          position: "fixed", inset: 0, zIndex: 99999,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(180deg, rgba(255,250,240,1) 0%, rgba(255,255,246,1) 60%, rgba(255,246,210,1) 100%)",
          pointerEvents: "auto", overflow: "hidden",
        }}
      >
        {!reducedMotion && (
          <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }} />
        )}

        <div className="splash-halo" aria-hidden="true" style={{ zIndex: 1 }} />

        <div ref={parallaxRef} style={{
          zIndex: 3, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          transform: "translate(0px,0px)", transition: "transform 180ms linear",
          willChange: "transform", pointerEvents: "none", gap: "24px",
        }}>
          <div ref={boxRef} style={{
            width: 220, height: 220, borderRadius: 9999, background: "white",
            boxShadow: "0 30px 60px rgba(16,24,40,0.12), 0 10px 24px rgba(16,24,40,0.06), 0 0 40px rgba(255,215,0,0.3), 0 0 80px rgba(255,215,0,0.15)",
            transformOrigin: "center",
            animation: stage === "enter" ? "logo-zoom 900ms cubic-bezier(.2,.9,.2,1) forwards" : stage === "hold" ? "logo-pulse 1500ms ease-in-out infinite, glow-pulse 2000ms ease-in-out infinite" : "none",
            WebkitAnimationFillMode: "forwards",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden", zIndex: 4, pointerEvents: "none",
          }}>
            <img src={logoImg} alt="St. Stephen's Church" style={{ width: "66%", height: "66%", objectFit: "contain", display: "block", willChange: "transform, opacity", filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.12))" }} />
          </div>

          <div className="splash-caption" style={{
            textAlign: "center", fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
            fontWeight: "700", fontSize: "20px", letterSpacing: "0.05em", lineHeight: "1.8",
            background: "linear-gradient(90deg, #caa85d 0%, #d7b870 50%, #caa85d 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            textShadow: "0 0 8px rgba(210, 180, 90, 0.35), 0 0 16px rgba(180, 140, 60, 0.25)",
            transformOrigin: "center",
            animation: stage === "enter" ? "fadeInCaption 1.3s ease forwards, captionPulse 2.5s ease-in-out infinite" : "none",
            WebkitAnimationFillMode: "forwards",
            transition: "transform 700ms cubic-bezier(.25,.46,.45,.94), opacity 650ms ease",
          }}>
            A warm community in Cumballa Hill.<br />Join us for Mass, prayer, and fellowship.
          </div>

          <div role="status" aria-live="polite" aria-label="Loading" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", position: "relative" }}>
            <div style={{
              position: "relative", width: 56, height: 56,
              animation: !loadingDone && stage === "hold" ? "loader-glow 1800ms ease-in-out infinite" : loadingDone ? "loader-glow-done 1800ms ease-in-out infinite" : "none",
            }}>
              <svg className={!loadingDone ? "spinner-rotate" : ""} viewBox="0 0 56 56" width="56" height="56" aria-hidden="true" style={{ display: "block" }}>
                <circle cx="28" cy="28" r="22" stroke="rgba(200,200,200,0.45)" strokeWidth="5" fill="none" />
                <circle cx="28" cy="28" r="22"
                  stroke={loadingDone ? "#16a34a" : "#8a8a8a"}
                  strokeWidth="5" strokeLinecap="round" fill="none"
                  strokeDasharray={CIRCUM}
                  strokeDashoffset={loadingDone ? 0 : Math.round(CIRCUM * 0.28)}
                  className={loadingDone ? "circle-done" : ""}
                  style={{ transition: loadingDone ? "stroke 260ms ease, stroke-dashoffset 520ms cubic-bezier(.2,.9,.2,1)" : "stroke-dashoffset 360ms linear", transformOrigin: "center" }}
                />
                <path d="M16 28 L24 36 L40 20" stroke="#16a34a" strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round"
                  style={{ strokeDasharray: 48, strokeDashoffset: loadingDone ? 0 : 48, transition: "stroke-dashoffset 420ms cubic-bezier(.2,.9,.2,1) 120ms", opacity: loadingDone ? 1 : 0 }}
                />
              </svg>
            </div>
            <div className={`check-text ${showWelcome ? "show" : ""}`} role="status" aria-live="polite">
              {showWelcome ? "Welcome" : ""}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}